import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Award } from "lucide-react";
import markDark from "../assets/logo-mark.png";
import api from "../lib/api";
import ReviewBox from "../components/ReviewBox";
import { LangMenu } from "../i18n";

export default function Certificate() {
    const { slug } = useParams();
    const [cert, setCert] = useState(null);
    const [img, setImg] = useState("");  // the designed certificate (template + name + RK's signature), when the course has one
    const [imgState, setImgState] = useState("loading"); // loading | ok | none (no design for this course -> simple certificate)
    const [pct, setPct] = useState(6);
    useEffect(() => { api.get(`/me/course/${slug}/certificate`).then((r) => setCert(r.data)).catch(() => setCert(false)); }, [slug]);
    useEffect(() => {
        if (!cert) return undefined;
        let url = "";
        let alive = true;
        const tick = setInterval(() => setPct((p) => (p < 92 ? p + Math.max(1, (92 - p) / 12) : p)), 250);
        const get = (tries) => api.get(`/me/course/${slug}/certificate/file`, { responseType: "blob" })
            .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setPct(100); setTimeout(() => { if (alive) { setImg(url); setImgState("ok"); } }, 250); })
            .catch((e) => { if (!alive) return; if (e.response?.status !== 404 && tries > 0) setTimeout(() => get(tries - 1), 1500); else setImgState("none"); });
        get(2);
        return () => { alive = false; clearInterval(tick); if (url) URL.revokeObjectURL(url); };
    }, [cert, slug]);
    const downloadPdf = async () => {
        const r = await api.get(`/me/course/${slug}/certificate/file`, { params: { fmt: "pdf" }, responseType: "blob" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `MuviArts-Certificate-${cert.certificate_id}.pdf`; a.click();
    };

    if (cert === false) return (
        <div className="min-h-screen grid place-items-center bg-cream text-center px-6">
            <div>
                <Award className="h-10 w-10 text-brand-gold mx-auto" />
                <p className="mt-4 text-neutral-600">Complete all lessons to unlock your certificate.</p>
                <Link to={`/learn/${slug}`} className="btn-primary mt-6 inline-flex">Go to course</Link>
            </div>
        </div>
    );
    if (!cert) return <div className="min-h-screen grid place-items-center bg-cream"><div className="h-8 w-8 rounded-full border-2 border-[#e8e1d4] border-t-ink animate-spin" /></div>;

    if (imgState === "loading") return (
        <div className="min-h-screen bg-cream py-8 px-4" data-testid="certificate-generating">
            <div className="mx-auto max-w-4xl mb-5"><Link to="/dashboard" className="inline-flex items-center gap-1.5 text-neutral-500 hover:text-ink text-sm"><ArrowLeft className="h-4 w-4" /> Dashboard</Link></div>
            <div className="mx-auto w-full max-w-4xl aspect-[1.413] rounded-xl bg-[#14100b] relative overflow-hidden shadow-lg grid place-items-center text-center px-6">
                <div className="absolute inset-3 sm:inset-5 border border-[#c8a24a]/35 rounded-lg pointer-events-none" />
                <div className="absolute inset-0 cert-shimmer" aria-hidden />
                <div className="relative w-full max-w-sm">
                    <Award className="h-9 w-9 sm:h-12 sm:w-12 mx-auto text-[#E5A83A]" />
                    <p className="mt-3 font-display text-lg sm:text-2xl text-[#F3EFE7]">Preparing your certificate</p>
                    <p className="mt-1 text-[11px] sm:text-sm text-[#A79F92]">Adding your name and RK's signature…</p>
                    <div className="mt-4 h-1.5 w-full bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#E5A83A] to-[#C88A24] transition-all duration-300" style={{ width: `${Math.round(pct)}%` }} /></div>
                    <p className="mt-2 text-[11px] text-[#A79F92]">{Math.round(pct)}%</p>
                </div>
            </div>
            <style>{`.cert-shimmer{background:linear-gradient(110deg,transparent 30%,rgba(229,168,58,.08) 50%,transparent 70%);background-size:200% 100%;animation:certsh 1.8s linear infinite}@keyframes certsh{to{background-position:-200% 0}}@media (prefers-reduced-motion:reduce){.cert-shimmer{animation:none}}`}</style>
        </div>
    );

    if (img) return (
        <div className="min-h-screen bg-cream py-8 px-4" data-testid="certificate-page">
            <div className="mx-auto max-w-4xl flex flex-wrap items-center justify-between gap-3 mb-5">
                <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-neutral-500 hover:text-ink text-sm"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
                <div className="flex items-center gap-2">
                    <LangMenu />
                    <a href={img} download={`MuviArts-Certificate-${cert.certificate_id}.png`} className="btn-outline !py-2.5 !px-5 text-xs" data-testid="download-cert-png">Image</a>
                    <button onClick={downloadPdf} className="btn-dark !py-2.5 !px-5 text-xs" data-testid="download-cert-btn"><Download className="h-4 w-4" /> PDF</button>
                </div>
            </div>
            <img src={img} alt={`Certificate of completion for ${cert.student_name}`} className="mx-auto w-full max-w-4xl rounded-xl shadow-lg" data-testid="certificate-body" />
            <p className="mt-4 text-center text-xs text-neutral-500">Share it on Instagram or LinkedIn and tag @muviartsofficial.</p>
            <ReviewBox slug={slug} />
        </div>
    );

    const date = cert.completed_at ? new Date(cert.completed_at).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

    return (
        <div className="min-h-screen bg-cream py-10 px-4" data-testid="certificate-page">
            <div className="mx-auto max-w-3xl flex items-center justify-between mb-6 print:hidden">
                <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-neutral-500 hover:text-ink text-sm"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
                <button onClick={() => window.print()} className="btn-dark !py-2.5 !px-5 text-xs" data-testid="download-cert-btn"><Download className="h-4 w-4" /> Download / Print</button>
            </div>

            <div className="mx-auto max-w-3xl bg-white border border-[#e0d6c2] shadow-sm p-10 sm:p-16 relative" data-testid="certificate-body">
                <div className="absolute inset-4 border border-brand-gold/40 pointer-events-none" />
                <div className="absolute inset-[22px] border border-brand-gold/20 pointer-events-none" />
                <div className="relative text-center">
                    <img loading="lazy" decoding="async" src={markDark} alt="MUVIARTS" className="h-14 mx-auto" />
                    <p className="mt-4 font-display text-2xl font-semibold tracking-tight">MUVIARTS</p>
                    <p className="overline mt-6">Certificate of Completion</p>
                    <div className="gold-rule w-24 mx-auto mt-4" />
                    <p className="mt-10 text-neutral-500">This certifies that</p>
                    <h1 className="mt-2 font-display italic text-4xl sm:text-5xl text-ink">{cert.student_name}</h1>
                    <p className="mt-6 text-neutral-500">has successfully completed the course</p>
                    <h2 className="mt-2 font-display font-semibold text-2xl sm:text-3xl text-brand-golddark">{cert.course_title}</h2>
                    <div className="mt-12 flex items-end justify-between text-left">
                        <div>
                            <p className="font-display italic text-lg">{cert.instructor}</p>
                            <div className="gold-rule w-40 mt-1" />
                            <p className="text-xs text-neutral-400 mt-1 uppercase tracking-wide">Instructor</p>
                        </div>
                        <div className="text-right">
                            <p className="font-display text-lg">{date}</p>
                            <div className="gold-rule w-40 mt-1 ml-auto" />
                            <p className="text-xs text-neutral-400 mt-1 uppercase tracking-wide">Date</p>
                        </div>
                    </div>
                    <p className="mt-10 text-[11px] text-neutral-400 tracking-wide">Certificate ID · {cert.certificate_id}</p>
                </div>
            </div>
            <div className="print:hidden"><ReviewBox slug={slug} /></div>
        </div>
    );
}
